import { config } from "../../../config";
import { AvailableModel, queryClaudeWithContext } from "../../../services/claude";
import { generateJsonSchema } from "../../../services/claude/schemaGenerator";
import { REQUEST_CANCELED_ERROR } from "../constants/errors";
import { ChatMessage, ChatState, ContextDataItem } from "../stores/chatTypes";
import { getEnlightenMeSystemPrompt } from "../utils/ragchatStorage";
import { calculateActualPromptTokens, estimateQueryTokens, estimateSingleContextTokenLength } from "./chunkSplitting";
import { saveJsonSchema } from "./schemaStorage";

const SCHEMA_GENERATION_TEMPERATURE = 0.3;
const MIN_SAMPLE_SIZE = 1;
const EMPTY_COUNT = 0;

type JsonSchema = unknown;

type GetState = () => ChatState & {
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
};

interface CalculateSampleSizeParams {
  totalTokens: number;
  totalItems: number;
  model: AvailableModel;
  query: string;
  systemPrompt: string;
  abortSignal?: AbortSignal;
}

const calculateSampleSize = async (params: CalculateSampleSizeParams): Promise<number> => {
  const { totalTokens, totalItems, model, query, systemPrompt, abortSignal } = params;

  const queryTokens = estimateQueryTokens(query);
  const actualPromptTokens = await calculateActualPromptTokens({
    systemPrompt,
    model,
    abortSignal
  });

  const adjustedSingleContextLength = estimateSingleContextTokenLength({
    totalTokensWithoutQuery: totalTokens,
    contextDataLength: totalItems,
    queryTokens
  });

  const maxTokensPerChunk = config.maxInputTokens;
  const availableTokensPerChunk = maxTokensPerChunk - actualPromptTokens;
  const conservativeItemsPerChunk = Math.floor(availableTokensPerChunk / adjustedSingleContextLength);
  const sampleSize = Math.max(conservativeItemsPerChunk, MIN_SAMPLE_SIZE);

  return Math.min(sampleSize, totalItems);
};

const selectRandomSample = <T>(array: T[], sampleSize: number): T[] => {
  if (sampleSize >= array.length) {
    return [...array];
  }

  const shuffled = [...array];
  Array.from({ length: sampleSize }, (_unused, i) => i).forEach((i) => {
    const j = i + Math.floor(Math.random() * (shuffled.length - i));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  });

  return shuffled.slice(EMPTY_COUNT, sampleSize);
};

export interface GenerateSchemaParams {
  content: string;
  contextData: ContextDataItem[];
  state: ChatState;
  temperature: number;
  get: GetState;
  abortSignal?: AbortSignal;
}

export interface SchemaGenerationResult {
  jsonSchema: JsonSchema | null;
  extractionName: string;
  initialResponse?: string;
}

interface SampleResponseParams {
  content: string;
  contextData: ContextDataItem[];
  state: ChatState;
  temperature: number;
  systemPrompt: string;
  get: GetState;
  abortSignal?: AbortSignal;
}

const buildAssistantMessage = (state: ChatState): { id: string; message: ChatMessage } => {
  const id = crypto.randomUUID();
  return {
    id,
    message: {
      id,
      content: "",
      role: "assistant",
      timestamp: new Date(),
      isStreaming: true,
      isThinking: state.thinkingMode,
      thinkingContent: ""
    }
  };
};

interface StreamHandlerParams {
  assistantMessageId: string;
  get: GetState;
  contentRef: { value: string };
}

const createStreamHandler = (params: StreamHandlerParams): ((delta: string) => void) => {
  const { assistantMessageId, get, contentRef } = params;
  return (delta: string): void => {
    contentRef.value += delta;
    get().updateMessage(assistantMessageId, {
      content: contentRef.value,
      isStreaming: true,
      isThinking: false
    });
  };
};

const createThinkingHandler = (assistantMessageId: string, get: GetState): ((delta: string, type: string) => void) =>
  (delta: string, type: string): void => {
    if (type !== "thinking") {
      return;
    }
    const currentMessage = get().messages.find((m) => m.id === assistantMessageId);
    if (currentMessage?.isThinking) {
      get().updateMessage(assistantMessageId, {
        thinkingContent: (currentMessage.thinkingContent ?? "") + delta
      });
    }
  };

const cancelOrRethrow = (error: unknown, abortSignal?: AbortSignal): never => {
  const errorToThrow = abortSignal?.aborted ? new Error(REQUEST_CANCELED_ERROR) : error;
  throw errorToThrow;
};

const runSampleQuery = async (
  params: SampleResponseParams,
  initialContextItems: ContextDataItem[],
  assistantMessageId: string
): Promise<string> => {
  const { content, state, temperature, systemPrompt, get, abortSignal } = params;
  const contentRef = { value: "" };

  try {
    const result = await queryClaudeWithContext({
      query: content,
      context: initialContextItems,
      messages: [],
      systemPrompt,
      temperature,
      maxTokens: state.maxTokens,
      budgetTokens: state.budgetTokens,
      effort: state.effort,
      model: state.model,
      thinkingMode: state.thinkingMode,
      onMessageDelta: createStreamHandler({ assistantMessageId, get, contentRef }),
      onThinkingDelta: createThinkingHandler(assistantMessageId, get)
    });

    if (!result.ok) {
      throw result.error;
    }

    const finalContent = result.response ?? contentRef.value;
    get().updateMessage(assistantMessageId, {
      content: finalContent,
      isStreaming: false
    });

    return finalContent;
  } catch (error) {
    get().updateMessage(assistantMessageId, {
      content: "Failed to generate sample response",
      isStreaming: false
    });
    return cancelOrRethrow(error, abortSignal);
  }
};

const generateSampleResponse = async (params: SampleResponseParams): Promise<string> => {
  const { content, contextData, state, systemPrompt, get, abortSignal } = params;

  if (!contextData || contextData.length === EMPTY_COUNT) {
    return "";
  }

  const sampleSize = await calculateSampleSize({
    totalTokens: state.totalCalculatedTokens,
    totalItems: contextData.length,
    model: state.model,
    query: content,
    systemPrompt,
    abortSignal
  });

  const initialContextItems = selectRandomSample(contextData, sampleSize);
  const { id: assistantMessageId, message } = buildAssistantMessage(state);
  get().addMessage(message);

  return runSampleQuery(params, initialContextItems, assistantMessageId);
};

interface SchemaFromResponseParams {
  initialResponse: string;
  query: string;
  model: string;
  get: GetState;
  abortSignal?: AbortSignal;
}

interface SchemaFromResponseResult {
  jsonSchema: JsonSchema | null;
  extractionName: string;
}

const buildStatusMessage = (): { id: string; message: ChatMessage } => {
  const id = crypto.randomUUID();
  return {
    id,
    message: {
      id,
      content: "Generating extraction schema...",
      role: "system",
      timestamp: new Date(),
      isStreaming: true,
      isThinking: false,
      thinkingContent: ""
    }
  };
};

const generateSchemaFromResponse = async (params: SchemaFromResponseParams): Promise<SchemaFromResponseResult> => {
  const { initialResponse, query, model, get, abortSignal } = params;

  const { id: statusMessageId, message: statusMessage } = buildStatusMessage();
  get().addMessage(statusMessage);

  try {
    const schemaResult = await generateJsonSchema({
      initialResponse,
      query,
      temperature: SCHEMA_GENERATION_TEMPERATURE,
      model
    });

    if (!schemaResult.ok) {
      throw schemaResult.error;
    }

    const jsonSchema = schemaResult.schema;
    const extractionName = "extract_data";

    saveJsonSchema(jsonSchema, query, extractionName);

    get().updateMessage(statusMessageId, {
      content: "Schema generated successfully",
      isStreaming: false
    });

    return { jsonSchema, extractionName };
  } catch (error) {
    if (abortSignal?.aborted) {
      throw new Error(REQUEST_CANCELED_ERROR);
    }

    console.warn("Failed to generate schema, using default approach:", error);
    get().updateMessage(statusMessageId, {
      content: "Using default extraction approach...",
      isStreaming: false
    });

    return { jsonSchema: null, extractionName: "extract_data" };
  }
};

export const generateSchemaFromInitialContext = async (params: GenerateSchemaParams): Promise<SchemaGenerationResult> => {
  const { content, contextData, state, temperature, get, abortSignal } = params;

  try {
    const systemPrompt = getEnlightenMeSystemPrompt();
    const sampleResponse = await generateSampleResponse({
      content,
      contextData,
      state,
      temperature,
      systemPrompt,
      get,
      abortSignal
    });

    if (!sampleResponse) {
      return { jsonSchema: null, extractionName: "extract_data" };
    }

    const { jsonSchema, extractionName } = await generateSchemaFromResponse({
      initialResponse: sampleResponse,
      query: content,
      model: state.model,
      get,
      abortSignal
    });

    return {
      jsonSchema,
      extractionName,
      initialResponse: sampleResponse
    };
  } catch (error) {
    if (abortSignal?.aborted) {
      throw new Error(REQUEST_CANCELED_ERROR);
    }

    console.warn("Schema generation process failed:", error);
    return { jsonSchema: null, extractionName: "extract_data" };
  }
};
